import React, {useMemo} from "react";
import {Admission} from "~/queries";
import {Card, CardContent, Divider, Grid, Typography} from "@mui/material";
import {formatDate, formatPhone} from "~/apps/core/utils";
import moment from "moment";
import {humanizeVehicleNumber, VehicleNumber} from "~/apps/core/ui/VehicleNumber";
import {TypographyWithLabel} from "~/apps/core/ui/TypographyWithLabel";
import {Tag} from "~/apps/core/ui/Tag";
import { CheckCircle } from "@mui/icons-material";
import {AdmissionStatusBadge} from "~/apps/packages/passes/components/AdmissionStatusBadge";
import {humanizeCarType} from "~/apps/packages/passes/utils";


export type AdmissionDetailProps = {
    admission: Admission
}


export const AdmissionDetail = (props: AdmissionDetailProps) => {
    const {admission, ...other} = props;

    const zones = useMemo(() => {
        const names = [
            "Зона 1 (дорога, офисы в порту)",
            "Зона 2 (причалы)",
            "Зона 3 (заграничные суда)",
        ]
        return names.map((value, index) => ({
            title: value,
            value: admission[`accessZone${index + 1}`]
        })).filter(x => x.value);
    }, [admission]);

    return (
        <Grid container direction="column" rowSpacing="8px" wrap="nowrap">
            <Grid item>
                <Card>
                    <CardContent>
                        <Grid container direction="column" wrap="nowrap" rowSpacing="8px">
                            <Grid item container direction="row" justifyContent="space-between">
                                <Grid item>
                                    <AdmissionStatusBadge>
                                        {admission.status}
                                    </AdmissionStatusBadge>
                                </Grid>
                                <Grid item>
                                    <Typography variant="cardText">
                                        {formatDate(moment(admission.passDate, "YYYY-MM-DD"))}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid item container direction="row" justifyContent="space-between">
                                <Grid item>
                                    <Typography variant="cardTitle2" color="#232326">
                                        <Tag>
                                            {admission.carRegNumber ? "Автомобильный" : "Пешеходный"}
                                        </Tag>
                                    </Typography>
                                </Grid>
                                {admission.carRegNumber && (
                                    <Grid item>
                                        <VehicleNumber number={admission.carRegNumber}/>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid item>
                                <Divider/>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="ФИО">
                                    {admission.fullName}
                                </TypographyWithLabel>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item>
                <Typography variant="screenText" color="primary">
                    Цель и зоны посещения
                </Typography>
            </Grid>

            <Grid item>
                <Card>
                    <CardContent>
                        <Grid container direction="column" wrap="nowrap" rowSpacing="8px">
                            <Grid item>
                                <TypographyWithLabel label="Цель посещения" noWrap={false}>
                                    {admission.companyActivityKind}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Пункт распоряжения правительства">
                                    {admission.paragraph}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Зоны доступа" noWrap={false}>
                                    <Grid container spacing="4px">
                                        {zones.map((zone, index) => (
                                            <Grid item key={index}>
                                                <Tag endIcon={<CheckCircle/>}>
                                                    {zone.title}
                                                </Tag>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </TypographyWithLabel>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item>
                <Typography variant="screenText" color="primary">
                    Компания
                </Typography>
            </Grid>

            <Grid item>
                <Card>
                    <CardContent>
                        <Grid container direction="column" wrap="nowrap" rowSpacing="8px">
                            <Grid item>
                                <TypographyWithLabel label="Юр.лицо">
                                    {admission.companyName}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="ИНН">
                                    {admission.inn}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Руководитель">
                                    {admission.chiefFullName}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Телефон руководителя">
                                    {formatPhone(admission.chiefPhone)}
                                </TypographyWithLabel>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item>
                <Typography variant="screenText" color="primary">
                    Личные данные
                </Typography>
            </Grid>

            <Grid item>
                <Card>
                    <CardContent>
                        <Grid container direction="column" wrap="nowrap" rowSpacing="8px">
                            <Grid item>
                                <TypographyWithLabel label="ФИО">
                                    {admission.fullName}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Должность">
                                    {admission.position}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Телефон">
                                    {formatPhone(admission.phone)}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Электронная почта">
                                    {admission.email}
                                </TypographyWithLabel>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>

            <Grid item>
                <Typography variant="screenText" color="primary">
                    Паспортные данные
                </Typography>
            </Grid>

            <Grid item>
                <Card>
                    <CardContent>
                        <Grid container direction="column" wrap="nowrap" rowSpacing="8px">
                            <Grid item>
                                <TypographyWithLabel label="Серия, номер">
                                    {admission.passportSeries} {admission.passportNumber}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Кем выдан" noWrap={false}>
                                    {admission.passportIssuer}
                                </TypographyWithLabel>
                            </Grid>
                            <Grid item>
                                <TypographyWithLabel label="Дата выдачи">
                                    {admission.passportDate}
                                </TypographyWithLabel>
                            </Grid>
                            {admission.birthDate && (
                                <Grid item>
                                    <TypographyWithLabel label="Дата рождения">
                                        {admission.birthDate}
                                    </TypographyWithLabel>
                                </Grid>
                            )}
                            {admission.birthPlace && (
                                <Grid item>
                                    <TypographyWithLabel label="Место рождения">
                                        {admission.birthPlace}
                                    </TypographyWithLabel>
                                </Grid>
                            )}
                            {admission.registrationAddress && (
                                <Grid item>
                                    <TypographyWithLabel label="Адрес регистрации" noWrap={false}>
                                        {admission.registrationAddress}
                                    </TypographyWithLabel>
                                </Grid>
                            )}
                            {admission.actualAddress && (
                                <Grid item>
                                    <TypographyWithLabel label="Адрес проживания" noWrap={false}>
                                        {admission.actualAddress}
                                    </TypographyWithLabel>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>


            {admission.carRegNumber && (
                <>
                    <Grid item>
                        <Typography variant="screenText" color="primary">
                            Автомобиль
                        </Typography>
                    </Grid>

                    <Grid item>
                        <Card>
                            <CardContent>
                                <Grid container direction="column" wrap="nowrap" rowSpacing="8px">
                                    <Grid item>
                                        <TypographyWithLabel label="Гос.номер">
                                            {humanizeVehicleNumber(admission.carRegNumber)}
                                        </TypographyWithLabel>
                                    </Grid>
                                    <Grid item>
                                        <TypographyWithLabel label="Тип автомобиля">
                                            {humanizeCarType(admission.carType)}
                                        </TypographyWithLabel>
                                    </Grid>
                                    <Grid item>
                                        <TypographyWithLabel label="Марка">
                                            {admission.carBrand}
                                        </TypographyWithLabel>
                                    </Grid>
                                    <Grid item>
                                        <TypographyWithLabel label="Модель">
                                            {admission.carModel}
                                        </TypographyWithLabel>
                                    </Grid>
                                    <Grid item>
                                        <TypographyWithLabel label="Цвет">
                                            {admission.carColor}
                                        </TypographyWithLabel>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </>
            )}
        </Grid>
    )
}